import React, { useMemo } from 'react';
import T from 'prop-types';
import { Button, List, Skeleton, Typography } from 'antd';
import clsx from 'clsx';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import emptyImg from '@assets/img/notifications/notification-empty.png';
import recapPaymentIcon from '@assets/icons/notifications/recap-payment.svg';
import transaksiIcon from '@assets/icons/notifications/transaksi.svg';
import IconCreate from '../../atoms/IconCreate';
import styles from './NotificationPanel.module.scss';

const { Text } = Typography;

const TAB_META = [
  { key: 'info', label: 'Info', kind: 'unicon', iconPath: 'icon/24px/unicon/megaphone-o', size: 24 },
  { key: 'tagihan', label: 'Tagihan', kind: 'unicon', iconPath: 'icon/24px/unicon/bill-o', size: 24 },
  // Figma: custom SVG (node 38574:43422)
  { key: 'transaksi', label: 'Transaksi', kind: 'svg', iconPath: transaksiIcon, size: 24 },
  { key: 'presensi', label: 'Presensi', kind: 'unicon', iconPath: 'icon/16px/unicon/user-check', size: 24 },
];

export default function NotificationPanel({
  items,
  loading,
  onReadAll,
  onClose,
  isMobile,
  onCategoryClick,
  onItemClick,
  onViewAll,
  unreadCount,
}) {
  const filtered = useMemo(() => items, [items]);
  const limited = useMemo(() => filtered.slice(0, 5), [filtered]);

  const isEmpty = items.length === 0;
  // Always show skeleton while a fetch is in-flight so the user never sees
  // stale data from a previous school/context (BNS-2814). The fetch is fast
  // enough that the brief skeleton ↔ list transition reads as "refreshing"
  // rather than flicker.
  const showSkeleton = loading;

  const renderSkeletonRow = (key) => (
    <div key={key} className={clsx(styles.item)} style={{ padding: '12px 18px' }}>
      <Skeleton.Avatar active shape="square" size={72} style={{ borderRadius: 12, marginRight: 12 }} />
      <div className={styles.itemBody} style={{ paddingTop: 4 }}>
        <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 2, width: ['90%', '40%'] }} />
      </div>
    </div>
  );

  const renderListIcon = (item) => {
    if (item.notifType === 'reminder_payment') {
      return <BanknotesIcon width={32} height={32} color="#FFFFFF" strokeWidth={1.5} />;
    }
    if (item.notifType === 'recap_payment') {
      return (
        <IconCreate
          svg={recapPaymentIcon}
          width={32}
          height={32}
          style={{ marginBottom: 0, marginRight: 0 }}
          injection={(svg) => {
            svg.classList.add('svg-class-name');
            const paths = [...svg.querySelectorAll('path')];
            paths.forEach((p) => {
              p.setAttribute('fill', '#FFFFFF');
              p.removeAttribute('stroke');
            });
            svg.setAttribute('width', '32');
            svg.setAttribute('height', '32');
          }}
        />
      );
    }

    if (item.iconKind === 'svg' && item.iconPath) {
      return (
        <IconCreate
          svg={item.iconPath}
          width={32}
          height={32}
          style={{ marginBottom: 0, marginRight: 0 }}
          injection={(svg) => {
            svg.classList.add('svg-class-name');
            const paths = [...svg.querySelectorAll('path')];
            paths.forEach((p) => {
              p.setAttribute('fill', '#FFFFFF');
              p.removeAttribute('stroke');
            });
            const lines = [...svg.querySelectorAll('line')];
            lines.forEach((l) => l.removeAttribute('stroke'));
            svg.setAttribute('width', '32');
            svg.setAttribute('height', '32');
          }}
        />
      );
    }

    return (
      <IconCreate
        svg={item.iconPath}
        width={32}
        height={32}
        color="#FFFFFF"
        style={{ marginBottom: 0, marginRight: 0 }}
        injection={(svg) => {
          svg.classList.add('svg-class-name');
          const paths = [...svg.querySelectorAll('path')];
          paths.forEach((p) => {
            p.setAttribute('fill', '#FFFFFF');
            p.setAttribute('stroke', '#FFFFFF');
          });
          const lines = [...svg.querySelectorAll('line')];
          lines.forEach((l) => l.setAttribute('stroke', '#FFFFFF'));
          svg.setAttribute('width', '32');
          svg.setAttribute('height', '32');
        }}
      />
    );
  };

  return (
    <div className={clsx(styles.root, isMobile && styles.mobile)}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <div className={styles.title}>Notifikasi</div>
          <Button
            type="link"
            className={styles.readAllTop}
            onClick={onReadAll}
            disabled={unreadCount <= 0}
            aria-disabled={unreadCount <= 0}
          >
            {unreadCount > 0 ? `Baca Semua (${unreadCount > 99 ? '99+' : unreadCount})` : 'Baca Semua'}
          </Button>
        </div>
        {!!onClose && (
          <Button type="text" className={styles.closeBtn} onClick={onClose} aria-label="Tutup">
            ×
          </Button>
        )}
      </div>

      <div className={styles.tabs}>
        {TAB_META.map(({ key, label, kind, iconPath, Icon, size = 22 }) => {
          const iconColor = 'var(--secondary-color, #E36711)';
          return (
            <button key={key} type="button" className={styles.categoryCard} onClick={() => onCategoryClick(key)}>
              <span className={styles.tabIcon}>
                {kind === 'heroicon' && Icon ? (
                  <Icon width={size} height={size} color={iconColor} />
                ) : kind === 'svg' ? (
                  <IconCreate
                    svg={iconPath}
                    width={size}
                    height={size}
                    style={{ marginBottom: 0, marginRight: 0 }}
                    injection={(svg) => {
                      svg.classList.add('svg-class-name');
                      const paths = [...svg.querySelectorAll('path')];
                      paths.forEach((p) => {
                        p.setAttribute('fill', 'var(--secondary-color, #E36711)');
                        p.removeAttribute('stroke');
                      });
                      const lines = [...svg.querySelectorAll('line')];
                      lines.forEach((l) => l.removeAttribute('stroke'));
                      svg.setAttribute('width', String(size));
                      svg.setAttribute('height', String(size));
                    }}
                  />
                ) : (
                  <IconCreate
                    svg={iconPath}
                    width={size}
                    height={size}
                    color={iconColor}
                    style={{ marginBottom: 0, marginRight: 0 }}
                  />
                )}
              </span>
              <span className={styles.tabLabel}>{label}</span>
            </button>
          );
        })}
      </div>

      <div className={styles.body}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionLabel}>Notifikasi baru diterima</div>
        </div>

        {showSkeleton ? (
          <div className={styles.listScroll} aria-busy="true" aria-live="polite">
            {[0, 1, 2].map((i) => renderSkeletonRow(`notif-skeleton-${i}`))}
          </div>
        ) : isEmpty ? (
          <div className={styles.empty}>
            <img className={styles.emptyImg} src={emptyImg} alt="" />
            <div className={styles.emptyText}>Belum ada notifikasi</div>
          </div>
        ) : (
          <div className={styles.listScroll}>
            <List
              className={styles.list}
              dataSource={limited}
              renderItem={(item) => (
                <List.Item
                  className={clsx(styles.item, !item.read && styles.itemUnread)}
                  onClick={() => onItemClick(item)}
                >
                  <div className={styles.itemIcon}>{renderListIcon(item)}</div>
                  <div className={styles.itemBody}>
                    <div className={styles.itemTop}>
                      <Text strong className={styles.itemTitle}>
                        {item.title}
                      </Text>
                      <Text className={styles.itemTime}>{item.time}</Text>
                    </div>
                    <Text className={styles.itemDesc}>{item.subtitle}</Text>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {/* BNS-2815: icon trimmed to 18px to match the slimmer footer bar (was 20px). */}
        <button type="button" className={styles.settingsBtn} aria-label="Pengaturan">
          <IconCreate
            svg="icon/24px/unicon/setting"
            width={18}
            height={18}
            color="#A6A9AC"
            style={{ marginBottom: 0, marginRight: 0 }}
          />
        </button>
        <Button type="link" className={styles.moreBtn} onClick={onViewAll}>
          Lihat Selengkapnya
        </Button>
      </div>
    </div>
  );
}

NotificationPanel.propTypes = {
  items: T.arrayOf(
    T.shape({
      id: T.string.isRequired,
      type: T.oneOf(['info', 'tagihan', 'transaksi', 'presensi']).isRequired,
      title: T.oneOfType([T.string, T.node]).isRequired,
      subtitle: T.oneOfType([T.string, T.node]).isRequired,
      content: T.oneOfType([T.string, T.node]),
      time: T.string.isRequired,
      read: T.bool,
      iconKind: T.oneOf(['unicon', 'heroicon', 'svg']).isRequired,
      iconPath: T.oneOfType([T.string, T.object]),
      notifType: T.string,
    })
  ).isRequired,
  loading: T.bool,
  onReadAll: T.func.isRequired,
  onClose: T.func,
  isMobile: T.bool,
  onCategoryClick: T.func,
  onItemClick: T.func,
  onViewAll: T.func,
  unreadCount: T.number,
};

NotificationPanel.defaultProps = {
  loading: false,
  onClose: null,
  isMobile: false,
  onCategoryClick: () => {},
  onItemClick: () => {},
  onViewAll: () => {},
  unreadCount: 0,
};
